/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';
import { Grid } from '@material-ui/core';
import PageContainer from 'components/PageContainer';
import { SsnField } from 'components/form';
import Button from 'components/Button';

import styles from './styles';

import { FormValues } from './Eligibility';
import { FormTextField } from 'components/TextField';
import { dateMask, dobPipe } from 'utils/masks';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
  Trans,
} from 'hooks/useTranslate/useTranslate';
import ActionButtonContainer, {
  TextAction,
} from 'components/ActionButtonsContainer';
import RestartLink from 'features/unified/form/RestartLink';
import Title from 'components/Title';
import BackToPrevious from 'components/unified/BackToPrevious';

type PropTypes<T extends FormValues> = FormikProps<T>;

export default function EligibilityView<T extends FormValues>(
  props: PropTypes<T>,
) {
  const { isValid, isSubmitting } = props;
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();

  return (
    <PageContainer maxWidth="sm">
      <Form noValidate>
        <div css={styles.headingContainer}>
          <Title>{tMd('unified.application.eligibility.title')}</Title>
        </div>
        <div css={styles.body}>
          {tMdMultiline('unified.application.eligibility.subtitle')}
        </div>
        <Field name="dateOfBirth">
          {(fieldProps: FieldProps) => (
            <FormTextField
              id="unifiedAppDOB"
              fieldProps={fieldProps}
              label={t('formLabel.dateOfBirth')}
              placeholder="mm/dd/yyyy"
              mask={dateMask}
              pipe={dobPipe}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
          )}
        </Field>
        <SsnField />
        <Grid item xs={12}>
          <div css={styles.finePrint}>
            {tMdMultiline('unified.application.eligibility.finePrint')}
          </div>
        </Grid>
        <ActionButtonContainer>
          <Button
            mobileFullWidth={true}
            id="billingAddressSubmit"
            type="submit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {tMd('unified.application.eligibility.submitButton')}
          </Button>
          <BackToPrevious tMdKey="unified.application.eligibility.backButton" />
          <TextAction>
            <div css={styles.restartText}>
              <Trans
                i18nKey="unified.application.eligibility.restartMessage"
                components={[<RestartLink />]}
              />
            </div>
          </TextAction>
        </ActionButtonContainer>
      </Form>
    </PageContainer>
  );
}
